import React from 'react';
import { getCachedGameState, clearGameCache } from '../utils/gameSync';

/**
 * HostErrorBoundary - Error boundary specifically for Host Mode with crash recovery
 * Provides "Reload & Resume" functionality when crashes occur
 */
class HostErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      gameId: null,
      canResume: false,
      lastSaveTime: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('🚨 Host Mode Error caught by boundary:', error, errorInfo);
    }

    // Try to extract gameId from the error or props
    let gameId = null;
    if (this.props.gameId) {
      gameId = this.props.gameId;
    } else if (window.location.pathname.includes('/launch/')) {
      // Extract from URL if not passed as prop
      const match = window.location.pathname.match(/\/launch\/([^/]+)/);
      if (match) {
        gameId = match[1];
      }
    }

    // Check if we can resume the game
    let canResume = false;
    let lastSaveTime = null;

    if (gameId) {
      try {
        const cachedState = getCachedGameState(gameId);
        if (cachedState && cachedState.lastSaved) {
          const saveTime = new Date(cachedState.lastSaved);
          const now = new Date();
          const hoursSinceSave = (now - saveTime) / (1000 * 60 * 60);
          
          // Can resume if save is less than 24 hours old
          if (hoursSinceSave < 24) {
            canResume = true;
            lastSaveTime = cachedState.lastSaved;
          }
        }
      } catch (err) {
        console.error('Error checking for resume data:', err);
      }
    }

    this.setState({
      errorInfo,
      gameId,
      canResume,
      lastSaveTime
    });

    // Log crash analytics (in production, you might want to send this to your analytics service)
    console.log('📊 Host Mode Crash Analytics:', {
      gameId,
      canResume,
      lastSaveTime,
      error: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });
  }

  handleReloadAndResume = () => {
    // Clear any corrupted cache
    if (this.state.gameId) {
      try {
        clearGameCache(this.state.gameId);
      } catch (err) {
        console.error('Error clearing cache:', err);
      }
    }
    
    // Reload the page
    window.location.reload();
  };

  handleReloadFresh = () => {
    // Clear all game cache
    if (this.state.gameId) {
      try {
        clearGameCache(this.state.gameId);
      } catch (err) {
        console.error('Error clearing cache:', err);
      }
    }
    
    // Navigate back to dashboard
    window.location.href = '/home';
  };

  handleReportIssue = () => {
    // Open support ticket with crash details
    const crashDetails = {
      gameId: this.state.gameId,
      error: this.state.error?.message,
      stack: this.state.error?.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    // Store crash details in localStorage for support ticket
    localStorage.setItem('lastCrashReport', JSON.stringify(crashDetails));
    
    // Navigate to support ticket page
    window.location.href = '/support-ticket?type=crash';
  };

  formatSaveTime = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8">
            <div className="text-center">
              <div className="text-8xl mb-6">🚨</div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Host Mode Crashed
              </h1>
              <p className="text-gray-600 mb-8 text-lg">
                Something went wrong while running your game. Don't worry - your progress is likely saved!
              </p>

              {/* Resume Information */}
              {this.state.canResume && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
                  <div className="flex items-center justify-center space-x-2 mb-3">
                    <span className="text-2xl">✅</span>
                    <h2 className="text-xl font-semibold text-green-900">
                      Game Can Be Resumed
                    </h2>
                  </div>
                  <p className="text-green-700 mb-3">
                    Your game was last saved at <strong>{this.formatSaveTime(this.state.lastSaveTime)}</strong>.
                  </p>
                  <p className="text-green-600 text-sm">
                    Game ID: {this.state.gameId}
                  </p>
                </div>
              )}

              {/* Error Details (Development Only) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
                  <h3 className="font-semibold text-red-900 mb-2">Error Details (Development):</h3>
                  <p className="text-red-700 text-sm font-mono">{this.state.error.message}</p>
                  {this.state.errorInfo && (
                    <details className="mt-2">
                      <summary className="text-red-600 text-sm cursor-pointer">Stack Trace</summary>
                      <pre className="text-red-600 text-xs mt-2 whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-4">
                {this.state.canResume && (
                  <button
                    onClick={this.handleReloadAndResume}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-6 rounded-lg text-lg transition duration-200 shadow-lg"
                  >
                    🔄 Reload & Resume Game
                  </button>
                )}
                
                <button
                  onClick={this.handleReloadFresh}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-lg text-lg transition duration-200 shadow-lg"
                >
                  🆕 Start Fresh Game
                </button>

                <button
                  onClick={this.handleReportIssue}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
                >
                  📧 Report This Issue
                </button>
              </div>

              {/* Helpful Tips */}
              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">💡 Helpful Tips:</h3>
                <ul className="text-gray-600 text-sm space-y-1 text-left">
                  <li>• Your game auto-saves every few seconds</li>
                  <li>• Try refreshing the page to resume</li>
                  <li>• Check your internet connection</li>
                  <li>• Close other browser tabs to free up memory</li>
                  <li>• Contact support if this keeps happening</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default HostErrorBoundary;
